import { Avatar, Badge, Box, Checkbox, Group, Paper, Stack, Text, useMantineColorScheme } from "@mantine/core";
import { ScoreGauge } from "../../../components/common/ScoreGauge";
import { useTranslation } from "../../../hooks/useTranslation";
import { formatShortDate } from "../../../utils/dateFormat";
import { toTitleCase } from "../../../utils/textFormat";
import { EmployeeOverviewListItem } from "../models/employeeOverview.model";
import { AllocationBadgeGroup } from "./AllocationBadgeGroup";

interface EmployeeOverviewCardProps {
    employee: EmployeeOverviewListItem;
    onClick: () => void;
    selectionMode?: boolean;
    isSelected?: boolean;
    onToggleSelect?: (employee: EmployeeOverviewListItem) => void;
}

const hasRealDate = (value: string | null): boolean =>
    !!value && new Date(value).getFullYear() > 1;

export function EmployeeOverviewCard({
    employee,
    onClick,
    selectionMode = false,
    isSelected = false,
    onToggleSelect,
}: EmployeeOverviewCardProps) {
    const { colorScheme } = useMantineColorScheme();
    const { t } = useTranslation();

    const handleCardClick = () => {
        if (selectionMode) {
            onToggleSelect?.(employee);
            return;
        }
        onClick();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleCardClick();
        }
    };

    const displayName = `${toTitleCase(employee.firstName)} ${toTitleCase(employee.lastName)}`;

    return (
        <Paper
            shadow="xs"
            withBorder
            radius="md"
            p="md"
            role="button"
            tabIndex={0}
            aria-label={t("EMPLOYEEOVERVIEW_OPEN_ARIA", { name: displayName }, `Open ${displayName}`)}
            onClick={handleCardClick}
            onKeyDown={handleKeyDown}
            style={{
                cursor: "pointer",
                position: "relative",
                overflow: "visible",
                outline: isSelected ? "2px solid var(--mantine-color-brand-5)" : undefined,
                outlineOffset: isSelected ? "2px" : undefined,
            }}
        >
            {/* Selection checkbox replaces the profile-completion gauge position while in
                selection mode — same treatment PersonCard.tsx uses. */}
            {selectionMode ? (
                <Box
                    style={{ position: "absolute", top: -10, left: 10, zIndex: 3 }}
                    onClick={(e) => { e.stopPropagation(); onToggleSelect?.(employee); }}
                >
                    <Checkbox
                        checked={isSelected}
                        onChange={() => onToggleSelect?.(employee)}
                        aria-label={t("EMPLOYEEOVERVIEW_SELECT_ARIA", { name: displayName }, `Select ${displayName}`)}
                        size="md"
                    />
                </Box>
            ) : (
                <Box style={{ position: "absolute", top: -10, left: 10, zIndex: 2 }}>
                    <ScoreGauge score={employee.profileCompletionScore} emptyLabel={t("EMPLOYEEOVERVIEW_NO_CV_EMPTY_LABEL", undefined, "No CV")} />
                </Box>
            )}

            {/* Status badges — dedicated top-right row, same treatment PersonCard.tsx uses, so
                these never compete with the name for space either. Reserved minHeight keeps cards
                aligned whether or not a badge is present. */}
            <Stack gap={2} mb={4} style={{ minHeight: 22 }}>
                <Group gap={4} justify="right" wrap="nowrap">
                    {hasRealDate(employee.availabilityDate) && (
                        <Badge color="orange.9" variant="filled" radius="sm" size="sm" style={{ textTransform: "none", position: "absolute", top: -6, right: 16, zIndex: 2 }}>
                            {t(
                                "EMPLOYEEOVERVIEW_AVAILABLE_BADGE",
                                { date: formatShortDate(employee.availabilityDate) },
                                `Available ${formatShortDate(employee.availabilityDate)}`
                            )}
                        </Badge>
                    )}
                    {!employee.isActive && (
                        <Badge color="gray.7" variant="filled" radius="sm" size="xs" style={{ textTransform: "none" }}>
                            {t("EMPLOYEEOVERVIEW_INACTIVE_BADGE", undefined, "Inactive")}
                        </Badge>
                    )}
                </Group>
            </Stack>

            <Group wrap="nowrap" align="flex-start" gap="sm" mb="xs">
                <Avatar
                    radius="xl"
                    color="brand"
                    src={employee.thumbnailBytes ? `data:image/jpeg;base64,${employee.thumbnailBytes}` : undefined}
                >
                    {employee.firstName.charAt(0)}
                    {employee.lastName.charAt(0)}
                </Avatar>
                <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={600} size="lg" truncate>
                        {displayName}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                        {employee.email}
                    </Text>
                </Box>
            </Group>

            <Stack gap={6} mb="xs">
                <AllocationBadgeGroup allocations={employee.allocations} />

                <Group gap={6}>
                    {employee.positionName ? (
                        <Badge color={colorScheme === "dark" ? "brand.1" : "brand"} variant="outline" radius="sm" style={{ textTransform: "none" }}>
                            {employee.positionName}
                        </Badge>
                    ) : (
                        <Badge color="gray" variant="outline" radius="sm">
                            {t("EMPLOYEEOVERVIEW_NO_POSITION_BADGE", undefined, "No position")}
                        </Badge>
                    )}
                    {employee.contractTypeName && (
                        <Badge color="gray" variant="light" radius="sm" style={{ textTransform: "none" }}>
                            {employee.contractTypeName}
                        </Badge>
                    )}
                </Group>

                {employee.workStartDate && (
                    <Text size="xs" c="dimmed">
                        {employee.workEndDate
                            ? t(
                                "EMPLOYEEOVERVIEW_WORK_RANGE",
                                { start: formatShortDate(employee.workStartDate), end: formatShortDate(employee.workEndDate) },
                                `Work: ${formatShortDate(employee.workStartDate)} – ${formatShortDate(employee.workEndDate)}`
                            )
                            : t(
                                "EMPLOYEEOVERVIEW_WORK_SINCE",
                                { start: formatShortDate(employee.workStartDate) },
                                `Work since ${formatShortDate(employee.workStartDate)}`
                            )}
                    </Text>
                )}
            </Stack>
        </Paper>
    );
}
